<template>
    <div class="trend-wrap">
        <!-- header -->
        <div class="trend-head">
            <span class="dash-panel-eyebrow">{{ chartTitle }}</span>
            <div class="trend-legend">
                <button
                    v-for="opt in modeOptions"
                    :key="opt.k"
                    class="trend-legend-btn"
                    :class="{ 'trend-legend-btn--on': mode === opt.k }"
                    @click="mode = opt.k"
                >
                    {{ opt.label }}
                </button>
            </div>
        </div>

        <!-- ECharts 컨테이너 -->
        <div class="trend-chart" ref="chartEl"></div>

        <!-- footer -->
        <div class="trend-foot">
            <div class="trend-foot-start">
                <div class="trend-foot-item">
                    <span class="trend-foot-swatch trend-foot-swatch--bar"></span>
                    <span>{{ isDaily ? monthPeriodLabel + " · 일별" : thisYear + "년 실측" }}</span>
                </div>
                <div v-if="!isDaily" class="trend-foot-item">
                    <span class="trend-foot-swatch trend-foot-swatch--missing"></span>
                    <span>미입력 (보고 필요)</span>
                </div>
                <div class="trend-foot-item">
                    <span class="trend-foot-swatch trend-foot-swatch--line"></span>
                    <span> 전{{ isDaily ? "월" : "년" }}</span>
                </div>
            </div>
            <span
                v-if="enteredCount > 0"
                :class="['trend-foot-delta', neg(ytdDelta, 'trend-foot-delta--down', 'trend-foot-delta--up')]"
            >
                <span class="trend-foot-delta-label">{{ footCompareLabel }}</span>
                {{ neg(ytdDelta, "▼", "▲") }}&thinsp;{{ Math.abs(ytdDelta).toFixed(1) }}%
            </span>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";
import { getDisplayUnitLabel } from "@/submodules/carbon-plugin/utils/formatters";

export default {
    name: "MonthlyTrendChart",
    props: {
        trendData: { type: Array, default: () => [] },
        prevTrend: { type: Array, default: () => [] },
        viewMode: { type: String, default: "YEAR", validator: (v) => ["YEAR", "MONTH"].includes(v) },
        selectedMonth: { type: String, default: null },
        cutoffMonth: { type: Number, default: () => new Date().getMonth() + 1 },
        cutoffDay: { type: Number, default: () => new Date().getDate() },
        cutoffYear: { type: Number, default: () => new Date().getFullYear() },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e" },
    },
    data() {
        return {
            chart: null,
            mode: "total",
            modeOptions: [
                { k: "total", label: "전체" },
                { k: "scope1", label: "Scope1" },
                { k: "scope2", label: "Scope2" },
                { k: "scope3", label: "Scope3" },
            ],
        };
    },
    computed: {
        isDaily() {
            return this.viewMode === "MONTH";
        },
        chartTitle() {
            return this.isDaily ? "일별 배출 추이" : "월별 배출 추이";
        },
        footCompareLabel() {
            return this.isDaily ? "전월 대비" : "전년 대비";
        },
        daysInMonth() {
            if (!this.selectedMonth || this.selectedMonth.length < 6) {
                return new Date(this.cutoffYear, this.cutoffMonth, 0).getDate();
            }
            const y = parseInt(this.selectedMonth.slice(0, 4), 10);
            const m = parseInt(this.selectedMonth.slice(4, 6), 10);
            return new Date(y, m, 0).getDate();
        },
        thisYear() {
            return this.cutoffYear;
        },
        prevYear() {
            return this.cutoffYear - 1;
        },
        modeLabel() {
            return this.modeOptions.find((o) => o.k === this.mode)?.label || this.mode;
        },
        unitShort() {
            return this.displayUnit === "tCO2e" ? "t" : "kg";
        },
        unitLabel() {
            return getDisplayUnitLabel(this.apiUnit, this.displayUnit);
        },

        trend() {
            if (this.isDaily) {
                const map = new Map();
                for (const d of this.trendData) {
                    const day = d.day ?? (d.period ? parseInt(String(d.period).slice(6, 8), 10) : null);
                    if (!day) continue;
                    const total = Number(d.total ?? d.t ?? 0);
                    map.set(day, {
                        index: day,
                        s1: Number(d.scope1 ?? d.s1 ?? 0),
                        s2: Number(d.scope2 ?? d.s2 ?? 0),
                        s3: Number(d.scope3 ?? d.s3 ?? 0),
                        t: total,
                        entered: Number.isFinite(total),
                    });
                }
                return Array.from({ length: this.daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const row = map.get(day);
                    return (
                        row || {
                            index: day,
                            s1: 0,
                            s2: 0,
                            s3: 0,
                            t: 0,
                            entered: false,
                        }
                    );
                });
            }

            const map = new Map();
            for (const d of this.trendData) {
                const month = d.month ?? (d.period ? parseInt(String(d.period).slice(4, 6), 10) : null);
                if (!month) continue;
                map.set(month, {
                    index: month,
                    s1: d.scope1 ?? d.s1 ?? 0,
                    s2: d.scope2 ?? d.s2 ?? 0,
                    s3: d.scope3 ?? d.s3 ?? 0,
                    t: d.total ?? d.t ?? 0,
                    entered: d.entered ?? (d.total > 0 || d.t > 0),
                });
            }
            return Array.from(
                { length: 12 },
                (_, i) => map.get(i + 1) || { index: i + 1, s1: 0, s2: 0, s3: 0, t: 0, entered: false },
            );
        },
        prevTrendNorm() {
            if (this.isDaily) {
                const map = new Map();
                for (const p of this.prevTrend) {
                    const day = p.d ?? p.day;
                    if (!day) continue;
                    map.set(day, { index: day, t: p.t ?? p.total ?? 0 });
                }
                return Array.from({ length: this.daysInMonth }, (_, i) => map.get(i + 1) || { index: i + 1, t: 0 });
            }

            const map = new Map();
            for (const p of this.prevTrend) {
                const month = p.m ?? p.month ?? (p.period ? parseInt(String(p.period).slice(4, 6), 10) : null);
                if (!month) continue;
                map.set(month, { index: month, t: p.t ?? p.total ?? 0 });
            }
            return Array.from({ length: 12 }, (_, i) => map.get(i + 1) || { index: i + 1, t: 0 });
        },
        enteredCount() {
            return this.trend.filter((d) => d.entered).length;
        },
        ytdNow() {
            const limit = this.isDaily ? this.cutoffDay : this.cutoffMonth;
            return this.trend.reduce((a, d, i) => a + (i + 1 <= limit && d.entered ? this.valueOf(d) : 0), 0);
        },
        ytdPrev() {
            const limit = this.isDaily ? this.cutoffDay : this.cutoffMonth;
            return this.prevTrendNorm.reduce((a, p, i) => a + (i + 1 <= limit && this.trend[i]?.entered ? p.t : 0), 0);
        },
        ytdDelta() {
            if (!this.ytdPrev) return 0;
            return ((this.ytdNow - this.ytdPrev) / this.ytdPrev) * 100;
        },
        maxValue() {
            return (
                Math.max(...this.trend.map((d) => this.valueOf(d)), ...this.prevTrendNorm.map((p) => p.t)) * 1.2 || 1
            );
        },
        niceMax() {
            const m = this.maxValue;
            if (m >= 100) return Math.ceil(m / 100) * 100;
            if (m >= 10) return Math.ceil(m / 10) * 10;
            return Math.ceil(m * 1000) / 1000;
        },
        overdueIdx() {
            const limit = this.isDaily ? this.cutoffDay : this.cutoffMonth;
            return this.trend.findIndex((d, i) => i + 1 < limit && !d.entered);
        },
        currentPeriodEntered() {
            const limit = this.periodLimit;
            return this.trend[limit - 1]?.entered;
        },
        periodLimit() {
            return this.isDaily ? this.cutoffDay : this.cutoffMonth;
        },
        xAxisLabels() {
            const len = this.isDaily ? this.daysInMonth : 12;
            return Array.from({ length: len }, (_, i) => (this.isDaily ? `${i + 1}일` : `${i + 1}월`));
        },
        monthPeriodLabel() {
            if (!this.selectedMonth || this.selectedMonth.length < 6) {
                return `${this.cutoffYear}년 ${this.cutoffMonth}월`;
            }
            return `${this.selectedMonth.slice(0, 4)}년 ${parseInt(this.selectedMonth.slice(4, 6), 10)}월`;
        },
    },
    watch: {
        mode() {
            this.updateChart();
        },
        trendData: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
        prevTrend: {
            handler() {
                this.updateChart();
            },
            deep: true,
        },
        cutoffDay() {
            this.updateChart();
        },
        viewMode() {
            this.updateChart();
        },
        selectedMonth() {
            this.updateChart();
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.chart = echarts.init(this.$refs.chartEl);
            this.updateChart();
        });
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        if (this.chart) this.chart.dispose();
    },
    methods: {
        neg(v, ifNeg, ifPos) {
            return v < 0 ? ifNeg : ifPos;
        },
        valueOf(d) {
            switch (this.mode) {
                case "scope1":
                    return d.s1;
                case "scope2":
                    return d.s2;
                case "scope3":
                    return d.s3;
                default:
                    return d.t;
            }
        },
        isFuture(i) {
            return i + 1 > this.periodLimit;
        },
        tickFmt(v) {
            if (this.maxValue >= 100) return Math.round(v).toLocaleString("ko-KR");
            return v.toFixed(3);
        },
        formatN(v) {
            if (v == null || isNaN(v)) return "—";
            if (this.maxValue >= 100) return Math.round(v).toLocaleString("ko-KR");
            return v.toFixed(4);
        },
        deltaFor(i) {
            const d = this.trend[i],
                p = this.prevTrendNorm[i];
            if (!d.entered || !p.t) return 0;
            return ((this.valueOf(d) - p.t) / p.t) * 100;
        },
        handleResize() {
            this.chart?.resize();
        },
        updateChart() {
            if (!this.chart) return;
            this.chart.setOption(this.buildOption(), true);
        },
        buildOption() {
            const trend = this.trend;
            const prevTrendNorm = this.prevTrendNorm;
            const niceMax = this.niceMax;
            const thisYear = this.thisYear;
            const prevYear = this.prevYear;
            const periodLimit = this.periodLimit;
            const xAxisLabels = this.xAxisLabels;
            const isDaily = this.isDaily;

            // ── Peak 탐지 ────────────────────────────────────────────────────
            const enteredVals = trend.map((d) => (d.entered ? this.valueOf(d) : -1));
            const peakIdx = enteredVals.indexOf(Math.max(...enteredVals));

            // ── Peak 바: 딥 다크 → 에메랄드 → 연 민트 (역방향, 녹색 계열) ────
            const peakGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#022c22" }, // 최상단: 딥 다크 그린
                { offset: 0.07, color: "#064e3b" }, // 어두운 면
                { offset: 0.32, color: "#047857" }, // 중간 에메랄드
                { offset: 0.68, color: "#10b981" }, // 밝은 에메랄드
                { offset: 1, color: "#d1fae5" }, // 하단: 연한 민트
            ]);

            const okColor = "#b2d8cd"; // 일반 입력 바 (차분한 민트)
            const missColor = "rgba(217,119,6,.13)"; // 미입력 stub

            // ── 바 데이터 ────────────────────────────────────────────────────
            const barData = trend.map((d, i) => {
                const isPeak = i === peakIdx && enteredVals[i] > 0;
                const isMiss = !d.entered && i + 1 < periodLimit;
                const isFuture = i + 1 > periodLimit;

                if (isFuture) return { value: null };

                if (isMiss && !isDaily) {
                    return {
                        value: niceMax * 0.035,
                        itemStyle: { color: missColor, borderRadius: [3, 3, 0, 0] },
                        label: {
                            show: true,
                            position: "top",
                            distance: 3,
                            formatter: "미입력",
                            color: "#d97706",
                            fontSize: 9,
                            fontWeight: 700,
                        },
                    };
                }

                if (isMiss && isDaily) {
                    return { value: 0, itemStyle: { color: "transparent" } };
                }

                const val = this.valueOf(d);
                return {
                    value: val,
                    itemStyle: {
                        color: isPeak ? peakGradient : okColor,
                        borderRadius: isPeak ? [10, 10, 3, 3] : [5, 5, 2, 2],
                        shadowColor: isPeak ? "rgba(16,185,129,.38)" : "transparent",
                        shadowBlur: isPeak ? 20 : 0,
                        shadowOffsetY: isPeak ? 6 : 0,
                    },
                    ...(isPeak && {
                        label: {
                            show: true,
                            position: "top",
                            distance: 5,
                            formatter: () => this.formatN(val),
                            color: "#047857",
                            fontSize: 10,
                            fontWeight: 800,
                        },
                    }),
                };
            });

            // ── Tooltip ───────────────────────────────────────────────────────
            const tooltipFmt = (params) => {
                const bar = params.find((p) => p.seriesIndex === 0);
                if (!bar) return "";
                const mi = bar.dataIndex;
                const d = trend[mi];
                const pv = prevTrendNorm[mi];
                const entered = d.entered;
                const val = entered ? this.valueOf(d) : null;
                const delta = this.deltaFor(mi);
                const periodUnit = isDaily ? "일" : "월";
                const status = entered ? "입력 완료" : mi + 1 < periodLimit ? "미입력" : "예정";
                const dColor = delta < 0 ? "#5eead4" : "#fca5a5";
                const dStr =
                    entered && pv.t
                        ? `<div style="font-size:11px;font-weight:700;color:${dColor};margin-top:3px">${delta < 0 ? "▼" : "▲"} ${Math.abs(delta).toFixed(1)}%</div>`
                        : "";
                return (
                    `<div style="min-width:160px">` +
                    `<div style="font-size:10px;color:#94a3b8;letter-spacing:.08em;margin-bottom:6px">${mi + 1}${periodUnit} · ${status}</div>` +
                    `<div style="font-size:19px;font-weight:700;letter-spacing:-.02em">${val != null ? this.formatN(val) : "—"}</div>` +
                    `<div style="font-size:10px;color:#9ba3aa;margin-top:1px">${thisYear} · ${this.unitShort}CO₂e</div>` +
                    dStr +
                    `<div style="border-top:1px solid #3a4651;margin:8px 0 5px"></div>` +
                    `<div style="display:flex;justify-content:space-between;font-size:12px">` +
                    `<span style="color:#9ba3aa">${isDaily ? "전월" : "전년"}</span>` +
                    `<span style="font-weight:600">${this.formatN(pv.t)}</span>` +
                    `</div></div>`
                );
            };

            return {
                backgroundColor: "transparent",
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "none" },
                    backgroundColor: "rgba(11,17,25,.92)",
                    borderColor: "transparent",
                    borderRadius: 12,
                    padding: [12, 16],
                    textStyle: { color: "#fff" },
                    formatter: tooltipFmt,
                },
                grid: { left: 10, right: 10, bottom: 0, top: 20, containLabel: true },
                xAxis: {
                    type: "category",
                    data: xAxisLabels,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: {
                        color: "#94a3b8",
                        fontSize: isDaily ? 9 : 11,
                        fontWeight: 500,
                        interval: isDaily ? 4 : 0,
                        formatter: (val, idx) => {
                            if (idx + 1 === periodLimit) return `{cur|${val}}`;
                            if (idx + 1 > periodLimit) return `{faint|${val}}`;
                            return val;
                        },
                        rich: {
                            cur: { color: "#11161a", fontWeight: 700, fontSize: 11 },
                            faint: { color: "#d1d5db", fontSize: 11 },
                        },
                    },
                },
                yAxis: {
                    type: "value",
                    max: niceMax,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: {
                        color: "#9ba3aa",
                        fontSize: 10,
                        formatter: (v) => this.tickFmt(v),
                    },
                    splitLine: { lineStyle: { color: "#f0f4f8" } },
                },
                series: [
                    {
                        name: `${thisYear}`,
                        type: "bar",
                        barMaxWidth: isDaily ? 12 : 36,
                        barCategoryGap: isDaily ? "24%" : "38%",
                        data: barData,
                        label: { show: false }, // 개별 아이템 label이 override
                    },
                    {
                        name: `${prevYear}`,
                        type: "line",
                        data: prevTrendNorm.map((p) => (p.t > 0 ? p.t : null)),
                        lineStyle: { color: "#6b7280", width: 1.5, opacity: 0.55 },
                        itemStyle: { color: "#fbf9f4", borderColor: "#6b7280", borderWidth: 1.5, opacity: 0.7 },
                        symbol: "circle",
                        symbolSize: 5,
                        connectNulls: false,
                        z: 2,
                    },
                ],
            };
        },
    },
};
</script>

<style scoped>
.trend-wrap {
    background: var(--dash-surface);
    border: 1px solid var(--dash-hairline-soft);
    border-radius: 14px;
    padding: 14px 18px 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    color: var(--dash-ink);
}
.trend-head {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    gap: 16px;
}
.trend-legend {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0;
    background: var(--dash-surface-2);
    border-radius: 999px;
    padding: 3px;
}
.trend-legend-btn {
    border: 0;
    background: transparent;
    padding: 5px 10px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: var(--dash-ink-mute);
    white-space: nowrap;
    font-family: inherit;
}
.trend-legend-btn:focus {
    outline: none;
}
.trend-legend-btn--on {
    color: var(--dash-ink);
    background: var(--dash-surface);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

/* ECharts 컨테이너 */
.trend-chart {
    flex: 1;
    min-height: 115px;
    width: 100%;
}

.trend-foot {
    flex-shrink: 0;
    margin-top: auto;
    padding-top: 8px;
    border-top: 1px solid var(--dash-hairline-soft);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 11px;
    color: var(--dash-ink-mute);
}
.trend-foot-start {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1;
    font-size: 12px;
    color: var(--dash-ink-soft);
}
.trend-foot-delta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
}
.trend-foot-delta--down {
    background: var(--dash-accent-wash);
    color: var(--dash-accent);
}
.trend-foot-delta--up {
    background: oklch(0.96 0.04 25);
    color: oklch(0.55 0.14 25);
}
.trend-foot-delta-label {
    font-weight: 500;
    opacity: 0.7;
}
.trend-foot-item {
    display: flex;
    align-items: center;
    gap: 6px;
}
.trend-foot-swatch {
    width: 12px;
    height: 12px;
    border-radius: 2px;
}
.trend-foot-swatch--bar {
    background: #b2d8cd;
}
.trend-foot-swatch--missing {
    background: rgba(217, 119, 6, 0.18);
    border: 1px solid rgba(217, 119, 6, 0.5);
}
.trend-foot-swatch--line {
    width: 18px;
    height: 2px;
    background: #6b7280;
    opacity: 0.6;
}
.trend-foot-status {
    font-size: 11px;
    color: var(--dash-ink-faint);
    white-space: nowrap;
}
</style>
